import { isValidSearchDate, SEARCH_INPUT_LIMITS } from '@agentwiki/shared'
import { parseLibraryQueryState, serializeLibraryQueryState } from './knowledge-library-query-state'

export type DocumentReturnPathname = '/doc' | '/search'

export interface DocumentReturnState {
  returnTo: {
    pathname: DocumentReturnPathname
    search: string
    focusId?: string
  }
}

export interface SafeDocumentReturnTarget {
  pathname: DocumentReturnPathname
  search: string
  href: string
  label: 'Knowledge' | 'Search results'
  focusId: string | null
}

const SEARCH_SOURCES = new Set(['all', 'docs', 'storage'])
const SEARCH_TYPES = new Set(['hybrid', 'keyword', 'semantic'])
const SAFE_FOCUS_ID = /^[A-Za-z0-9_-]{1,200}$/

function safeText(value: string | null, maxLength: number) {
  if (!value) return null
  const trimmed = value.trim()
  return trimmed && trimmed.length <= maxLength && !/[\u0000-\u001f\u007f]/.test(trimmed)
    ? trimmed
    : null
}

export function sanitizeSearchParams(params: URLSearchParams) {
  const sanitized = new URLSearchParams()
  const query = safeText(params.getAll('q')[0] ?? null, SEARCH_INPUT_LIMITS.query)
  const source = params.getAll('source')[0]
  const type = params.getAll('type')[0]
  const category = safeText(params.getAll('category')[0] ?? null, SEARCH_INPUT_LIMITS.category)
  const dateFrom = params.getAll('dateFrom')[0]
  const dateTo = params.getAll('dateTo')[0]

  if (query) sanitized.set('q', query)
  if (source && SEARCH_SOURCES.has(source)) sanitized.set('source', source)
  if (type && SEARCH_TYPES.has(type)) sanitized.set('type', type)
  if (category) sanitized.set('category', category)
  for (const tag of params.getAll('tag').slice(0, SEARCH_INPUT_LIMITS.tags)) {
    const safeTag = safeText(tag, SEARCH_INPUT_LIMITS.tag)
    if (safeTag) sanitized.append('tag', safeTag)
  }
  if (dateFrom && isValidSearchDate(dateFrom)) sanitized.set('dateFrom', dateFrom)
  if (dateTo && isValidSearchDate(dateTo)) sanitized.set('dateTo', dateTo)
  return sanitized
}

function returnState(
  pathname: DocumentReturnPathname,
  params: URLSearchParams,
  focusId?: string,
): DocumentReturnState {
  const serialized = params.toString()
  return {
    returnTo: {
      pathname,
      search: serialized ? `?${serialized}` : '',
      ...(focusId && SAFE_FOCUS_ID.test(focusId) ? { focusId } : {}),
    },
  }
}

export function buildKnowledgeReturnState(params: URLSearchParams, focusId?: string) {
  return returnState('/doc', serializeLibraryQueryState(parseLibraryQueryState(params)), focusId)
}

export function buildSearchReturnState(params: URLSearchParams, focusId?: string) {
  return returnState('/search', sanitizeSearchParams(params), focusId)
}

function fallbackTarget(): SafeDocumentReturnTarget {
  return { pathname: '/doc', search: '', href: '/doc', label: 'Knowledge', focusId: null }
}

export function getSafeDocumentReturnTarget(state: unknown): SafeDocumentReturnTarget {
  if (!state || typeof state !== 'object' || !('returnTo' in state)) return fallbackTarget()
  const candidate = state.returnTo
  if (!candidate || typeof candidate !== 'object' || !('pathname' in candidate)) {
    return fallbackTarget()
  }
  if (candidate.pathname !== '/doc' && candidate.pathname !== '/search') return fallbackTarget()
  const rawSearch =
    'search' in candidate && typeof candidate.search === 'string' ? candidate.search : ''
  if (rawSearch && !rawSearch.startsWith('?')) return fallbackTarget()

  const rawParams = new URLSearchParams(rawSearch.slice(1))
  const params =
    candidate.pathname === '/doc'
      ? serializeLibraryQueryState(parseLibraryQueryState(rawParams))
      : sanitizeSearchParams(rawParams)
  const search = params.size ? `?${params.toString()}` : ''
  const rawFocusId =
    'focusId' in candidate && typeof candidate.focusId === 'string' ? candidate.focusId : ''
  const focusId = SAFE_FOCUS_ID.test(rawFocusId) ? rawFocusId : null

  return {
    pathname: candidate.pathname,
    search,
    href: `${candidate.pathname}${search}`,
    label: candidate.pathname === '/search' ? 'Search results' : 'Knowledge',
    focusId,
  }
}
